/**
 * @file axios 请求核心实例
 * @description 统一处理业务响应码、登录态跳转、请求并发槽位、错误提示和后端 Id/id 字段兼容。
 */
import axios, {
  type AxiosInstance,
  type InternalAxiosRequestConfig,
  type AxiosResponse,
  type AxiosPromise
} from 'axios'
import router from '@/router'
import { getToken } from '@/stores/modules/token'
import { extractAxiosErrorMessage } from '@/utils/errorMessage'
import { showAppError } from '@/utils/showError'
import {
  logDevApiTiming,
  parseServerTiming,
  readApiTiming
} from '@/utils/apiTiming'
import {
  acquireRequestSlot,
  releaseRequestSlot,
  type RequestPriority
} from '@/utils/requestScheduler'
import { deferBackgroundRequest } from '@/utils/requestPriority'
import { BusinessResponseError } from '@/utils/expectedErrors'
import {
  normalizeApiResponse,
  type ApiResponse
} from '@/types/apiResponse'

import type { AxiosRequestConfig, AxiosError } from 'axios'

interface CustomAxiosRequestConfig extends InternalAxiosRequestConfig {
  /** 是否关闭全局错误提示 */
  suppressError?: boolean
  /** 请求优先级：交互 > 普通 > 背景 */
  requestPriority?: RequestPriority
  /** 是否已占用并发槽位 */
  _slotAcquired?: boolean
}

const baseURL = import.meta.env.VITE_BASE_API // 使用配置文件中的后端URL地址
const baseImgUrl = import.meta.env.VITE_IMG_HOST // 使用配置文件中的图片URL地址
const imgFallbackHost = import.meta.env.VITE_IMG_FALLBACK_HOST // CDN 失败时的同源反代地址
const ossImageBaseUrl = import.meta.env.VITE_OSS_IMAGE_BASE_URL
const localImagePrefix = import.meta.env.VITE_LOCAL_IMAGE_PREFIX
const sentryDsn =
  import.meta.env.VITE_BUGSINK_DSN || import.meta.env.VITE_SENTRY_DSN || ''

/** 后端 JSON 响应可携带的递归数据结构。 */
type ApiPayload =
  | string
  | number
  | boolean
  | null
  | undefined
  | ApiPayload[]
  | { [key: string]: ApiPayload }

/** 后端对象型 JSON 响应。 */
type ApiPayloadObject = { [key: string]: ApiPayload }

const ERROR_CODE = {
  SUCCESS: 200,
  SYSTEM_ERROR: 500,
  LOGIN_REQUIRED: 401, // 登录过期
  UNIQUE_VIOLATION: 1002, // 重复键冲突
  DOWNLOAD_UNAUTHENTIC: 1003 // 没有权限下载文件
} as const

/**
 * 单层 Id→id 归一（用于分页 records，避免深层递归）
 */
const normalizeEntityIdsShallow = (value: ApiPayload): ApiPayload => {
  if (value === null || value === undefined || typeof value !== 'object') {
    return value
  }
  if (Array.isArray(value)) {
    return value.map(normalizeEntityIdsShallow)
  }
  const record = value as ApiPayloadObject
  const normalized: ApiPayloadObject = { ...record }
  if (normalized.id === undefined && normalized.Id !== undefined) {
    normalized.id = normalized.Id
  }
  return normalized
}

/**
 * 将 Rust 后端序列化的 `Id` 归一为前端使用的 `id`（递归处理嵌套对象与数组）
 */
const normalizeEntityIds = (value: ApiPayload): ApiPayload => {
  if (value === null || value === undefined) {
    return value
  }
  if (Array.isArray(value)) {
    return value.map(normalizeEntityIds)
  }
  if (typeof value === 'object') {
    const record = value as ApiPayloadObject
    const normalized: ApiPayloadObject = {}
    for (const [key, val] of Object.entries(record)) {
      normalized[key] = normalizeEntityIds(val)
    }
    if (normalized.id === undefined && normalized.Id !== undefined) {
      normalized.id = normalized.Id
    }
    return normalized
  }
  return value
}

/** 分页大列表仅浅层归一 records，全量数组（如 members/all）亦浅层归一 */
const normalizeApiResponseIds = (
  payload: ApiResponse<ApiPayload>
): ApiResponse<ApiPayload> => {
  const data = payload.data
  if (Array.isArray(data)) {
    return {
      ...payload,
      data: data.map(normalizeEntityIdsShallow)
    }
  }
  if (!data || typeof data !== 'object') {
    return payload
  }
  const page = data as ApiPayloadObject
  if (!Array.isArray(page.records)) {
    return {
      ...payload,
      data: normalizeEntityIds(data)
    }
  }
  return {
    ...payload,
    data: {
      ...page,
      records: page.records.map(normalizeEntityIdsShallow)
    }
  }
}

const showErrorMessage = (
  config: AxiosRequestConfig,
  message: string
): void => {
  const suppressError = (config as CustomAxiosRequestConfig)?.suppressError
  if (!suppressError) {
    showAppError(message)
  }
}

/** 创建带标记的业务响应错误，避免普通对象进入全局异常上报 */
const createBusinessResponseError = (
  payload: ApiResponse<ApiPayload>,
  fallbackMessage = '业务请求失败'
): BusinessResponseError => {
  return new BusinessResponseError(payload, fallbackMessage)
}

const isRawBinaryResponse = (res: AxiosResponse): boolean => {
  const rt = res.config.responseType
  return (
    rt === 'blob' ||
    rt === 'arraybuffer' ||
    res.data instanceof Blob ||
    res.data instanceof ArrayBuffer
  )
}

// 规范化处理后端业务响应
const handleBusinessResponse = (
  res: AxiosResponse<ApiResponse<ApiPayload>>
):
  | AxiosResponse<ApiResponse<ApiPayload>>
  | Promise<AxiosResponse<ApiResponse<ApiPayload>>> => {
  if (isRawBinaryResponse(res)) {
    return res
  }

  if (
    !res.data ||
    typeof res.data !== 'object' ||
    typeof res.data.code !== 'number'
  ) {
    return res
  }

  const normalized = normalizeApiResponse(res.data)
  res.data = normalizeApiResponseIds(normalized)
  const { code, msg } = res.data

  // 处理业务层面的响应码
  switch (code) {
    case ERROR_CODE.SUCCESS:
      return res
    case ERROR_CODE.LOGIN_REQUIRED:
      // 登录态过期，跳转登录
      router.push('/login')
      return Promise.reject(createBusinessResponseError(res.data, '请先登录'))
    case ERROR_CODE.SYSTEM_ERROR:
      // 系统异常
      showErrorMessage(res.config, msg || '服务异常0')
      return Promise.reject(createBusinessResponseError(res.data, '服务异常0'))
    default:
      break
  }

  // 其他业务错误
  showErrorMessage(res.config, msg || '服务异常1')
  return Promise.reject(createBusinessResponseError(res.data, '服务异常1'))
}

// 处理传输层或非 2xx 响应错误
const handleAxiosError = (
  err: AxiosError<ApiResponse<ApiPayload>>
): Promise<never> => {
  if (err.response?.status === 401) {
    router.push('/login')
  }

  captureAxiosError(err)
  const message = extractAxiosErrorMessage(err)
  if (err.config) {
    showErrorMessage(err.config, message)
  } else {
    showAppError(message)
  }
  return Promise.reject(err)
}

/** 判断是否为后台请求的超时或网络抖动噪音 */
const isBackgroundNetworkNoise = (
  err: AxiosError<ApiResponse<ApiPayload>>
): boolean => {
  const config = err.config as CustomAxiosRequestConfig | undefined
  if (config?.requestPriority !== 'background') return false
  if (err.response) return false

  const message = err.message || ''
  return (
    err.code === 'ECONNABORTED' ||
    err.code === 'ETIMEDOUT' ||
    err.code === 'ERR_NETWORK' ||
    message === 'Network Error' ||
    message.toLowerCase().includes('timeout')
  )
}

/** 异步上报请求异常，避免 Sentry SDK 进入首屏同步依赖 */
const captureAxiosError = (
  err: AxiosError<ApiResponse<ApiPayload>>
): void => {
  if (!sentryDsn || !import.meta.env.PROD) {
    return
  }
  if (isBackgroundNetworkNoise(err)) {
    return
  }
  void import('@sentry/vue')
    .then((Sentry) => {
      Sentry.captureException(err)
    })
    .catch(() => undefined)
}

/** 从请求配置读取优先级，供释放槽位时使用 */
const customConfigPriority = (
  config?: CustomAxiosRequestConfig
): RequestPriority => config?.requestPriority ?? 'normal'

/** 响应完成后释放槽位并打印 dev 耗时分解 */
const finalizeRequest = (config?: CustomAxiosRequestConfig): void => {
  if (config?._slotAcquired) {
    config._slotAcquired = false
    releaseRequestSlot(customConfigPriority(config))
  }
}

/** 异步读取 PerformanceResourceTiming 与 Server-Timing 并输出 */
const reportApiTiming = (
  config: CustomAxiosRequestConfig,
  responseUrl?: string,
  serverTimingHeader?: string | null
): void => {
  if (!import.meta.env.DEV) {
    return
  }
  const url =
    responseUrl ||
    (config.baseURL && config.url
      ? new URL(config.url, config.baseURL).href
      : config.url || '')
  if (!url && !serverTimingHeader) {
    return
  }
  window.setTimeout(() => {
    const record = url
      ? readApiTiming(url, (config.method || 'GET').toUpperCase())
      : null
    const server = parseServerTiming(serverTimingHeader)
    if (record || server.length > 0) {
      logDevApiTiming(record, server)
    }
  }, 0)
}

const instance: AxiosInstance = axios.create({
  // TODO 1. 基础地址，超时时间
  baseURL,
  timeout: 10000
})

// 请求拦截器
instance.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    const customConfig = config as CustomAxiosRequestConfig
    const priority = customConfig.requestPriority ?? 'normal'
    if (priority === 'background') {
      await deferBackgroundRequest()
    }
    await acquireRequestSlot(priority)
    customConfig._slotAcquired = true

    // 2. 携带token
    const token = getToken()
    if (token) {
      config.headers.Authorization = token
    }

    const isFormDataRequest =
      typeof FormData !== 'undefined' && config.data instanceof FormData

    if (isFormDataRequest) {
      const ensureJsonBlobPart = (formData: FormData, key: string): void => {
        const part = formData.get(key)
        if (typeof part !== 'string') return
        const text = part.trim()
        if (!text) return
        try {
          JSON.parse(text)
        } catch {
          return
        }
        formData.set(key, new Blob([text], { type: 'application/json' }))
      }

      ensureJsonBlobPart(config.data as FormData, 'metadata')
      ensureJsonBlobPart(config.data as FormData, 'mangaBenefit')

      if (config.headers) {
        delete config.headers['Content-Type']
        delete config.headers['content-type']
      }
    } else {
      if (config.headers) {
        const hasContentTypeHeader =
          config.headers['Content-Type'] || config.headers['content-type']

        if (!hasContentTypeHeader) {
          config.headers['Content-Type'] = 'application/json'
        }
      }
    }

    return config
  },
  (err) => Promise.reject(err)
)

// 响应拦截器
instance.interceptors.response.use(
  (res: AxiosResponse<ApiResponse<ApiPayload>>) => {
    reportApiTiming(
      res.config as CustomAxiosRequestConfig,
      res.request?.responseURL,
      res.headers['server-timing']
    )
    finalizeRequest(res.config as CustomAxiosRequestConfig)
    return handleBusinessResponse(res)
  },
  (err: AxiosError<ApiResponse<ApiPayload>>) => {
    reportApiTiming(
      err.config as CustomAxiosRequestConfig,
      err.request?.responseURL,
      err.response?.headers?.['server-timing']
    )
    finalizeRequest(err.config as CustomAxiosRequestConfig)
    return handleAxiosError(err)
  }
)

export default instance
export {
  baseURL,
  baseImgUrl,
  imgFallbackHost,
  ossImageBaseUrl,
  localImagePrefix
}
export type { CustomAxiosRequestConfig }

export const requestWithUpload = <T = ApiResponse<unknown>>(
  url: string,
  data: FormData,
  method: 'post' | 'put' = 'post'
): AxiosPromise<T> => {
  return instance({
    url,
    method,
    data,
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  })
}
